import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

import { errorActions } from "../../store/error";
import useLocalization from "../../hooks/useLocalization";

const GlobalErrorHandler = () => {
  const dispatch = useDispatch();
  const { getMessage, getLabel } = useLocalization();
  const { message, fieldErrors, severity } = useSelector(
    (state) => state.error,
  );

  // Simple errors (no fieldErrors): show toast/snackbar
  useEffect(() => {
    // Nothing to do here; Snackbar is controlled by state
  }, [message, fieldErrors]);

  const hasFieldErrors = Array.isArray(fieldErrors) && fieldErrors.length > 0;
  const hasMessage = !!message;

  const getDialogTitle = () => {
    if (message && message !== "fieldErrors") {
      return getMessage(message);
    }

    return getMessage("fieldErrors") || "Validation errors";
  };

  const getFieldMessage = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "Invalid value";

    const localized = getMessage(raw);
    if (localized !== raw) return localized;

    if (raw === "isEmpty") return "is required";
    if (raw === "alreadyExists") return "already exists";

    return raw;
  };

  const handleClose = () => {
    dispatch(errorActions.clearError());
  };

  return (
    <>
      {/* Field errors -> modal with list */}
      <Dialog
        open={hasFieldErrors}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent dividers>
          <List dense>
            {fieldErrors.map((err, idx) => (
              <ListItem key={idx}>
                <ListItemText
                  primary={getLabel(err?.name || "Field")}
                  secondary={getFieldMessage(err?.message)}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Simple error -> snackbar */}
      <Snackbar
        open={hasMessage && !hasFieldErrors}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={severity || "error"}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GlobalErrorHandler;
